// src/context/BookingContext.js - Complete with extras, passenger data, and service type

import React, { createContext, useReducer, useCallback, useMemo } from 'react';
import { PAGES, SERVICE_TYPES } from '../utils/constants';

const BookingContext = createContext();

const initialState = {
  currentPage: PAGES.HOME,
  selectedService: SERVICE_TYPES.AIRPORT,  // Added - shared service type
  searchData: null,
  selectedVehicle: null,
  routeInfo: null,
  extrasData: null,
  passengerData: null,
  bookingDetails: null,
  manageBookingId: null,
  loading: false,
  error: null
};

const bookingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_SELECTED_SERVICE':           // Added
      return { ...state, selectedService: action.payload };
    case 'SET_SEARCH_DATA':
      return { ...state, searchData: action.payload };
    case 'SET_SELECTED_VEHICLE':
      return { ...state, selectedVehicle: action.payload };
    case 'SET_ROUTE_INFO':
      return { ...state, routeInfo: action.payload };
    case 'SET_EXTRAS_DATA':
      return { ...state, extrasData: action.payload };
    case 'SET_PASSENGER_DATA':
      return { ...state, passengerData: action.payload };
    case 'SET_BOOKING_DETAILS':
      const newDetails = typeof action.payload === 'function'
        ? action.payload(state.bookingDetails)
        : action.payload;
      return { ...state, bookingDetails: newDetails };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET_BOOKING':
      return { ...initialState };
    case 'SET_MANAGE_BOOKING_ID':
      return { ...state, manageBookingId: action.payload };
    default:
      return state;
  }
};

export const BookingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  const setCurrentPage = useCallback((page) => dispatch({ type: 'SET_PAGE', payload: page }), []);
  const setSelectedService = useCallback((service) => dispatch({ type: 'SET_SELECTED_SERVICE', payload: service }), []);
  const setSearchData = useCallback((data) => dispatch({ type: 'SET_SEARCH_DATA', payload: data }), []);
  const setSelectedVehicle = useCallback((vehicle) => dispatch({ type: 'SET_SELECTED_VEHICLE', payload: vehicle }), []);
  const setRouteInfo = useCallback((info) => dispatch({ type: 'SET_ROUTE_INFO', payload: info }), []);
  const setExtrasData = useCallback((data) => dispatch({ type: 'SET_EXTRAS_DATA', payload: data }), []);
  const setPassengerData = useCallback((data) => dispatch({ type: 'SET_PASSENGER_DATA', payload: data }), []);
  const setBookingDetails = useCallback((details) => dispatch({ type: 'SET_BOOKING_DETAILS', payload: details }), []);
  const setLoading = useCallback((loading) => dispatch({ type: 'SET_LOADING', payload: loading }), []);
  const setError = useCallback((error) => dispatch({ type: 'SET_ERROR', payload: error }), []);
  const resetBooking = useCallback(() => dispatch({ type: 'RESET_BOOKING' }), []);
  const setManageBookingId = useCallback((id) => dispatch({ type: 'SET_MANAGE_BOOKING_ID', payload: id }), []);

  const value = useMemo(() => ({
    state,
    dispatch,
    setCurrentPage,
    setSelectedService,
    setSearchData,
    setSelectedVehicle,
    setRouteInfo,
    setExtrasData,
    setPassengerData,
    setBookingDetails,
    setLoading,
    setError,
    resetBooking,
    setManageBookingId
  }), [
    state,
    setCurrentPage,
    setSelectedService,
    setSearchData,
    setSelectedVehicle,
    setRouteInfo,
    setExtrasData,
    setPassengerData,
    setBookingDetails,
    setLoading,
    setError,
    resetBooking,
    setManageBookingId
  ]);

  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
};

export default BookingContext;